<template>
    <div class="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 dark:from-secondary-900 dark:to-secondary-800 flex items-center justify-center px-4">
        <div class="max-w-md w-full">
            <!-- Card -->
            <div class="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                <!-- Icon Section -->
                <div class="bg-gradient-to-r from-red-500 to-red-600 p-8 text-center">
                    <div class="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h1 class="text-2xl font-bold text-white">
                        Company {{ statusLabel }}
                    </h1>
                </div>

                <!-- Content -->
                <div class="p-8">
                    <!-- Company Name -->
                    <div class="mb-6 text-center">
                        <h2 class="text-xl font-semibold text-secondary-900 dark:text-white">
                            {{ company.name }}
                        </h2>
                        <span
                            :class="statusBadgeClass"
                            class="inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium"
                        >
                            {{ statusLabel }}
                        </span>
                    </div>

                    <!-- Owner View -->
                    <div v-if="company.is_owner" class="space-y-4">
                        <div class="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                            <div class="flex items-start">
                                <svg class="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                </svg>
                                <div class="flex-1">
                                    <h3 class="text-sm font-semibold text-orange-900 dark:text-orange-200 mb-1">
                                        Subscription Required
                                    </h3>
                                    <p class="text-sm text-orange-800 dark:text-orange-300">
                                        Your subscription has expired. Reactivate to restore full access for you and your team.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="space-y-3">
                            <p class="text-secondary-600 dark:text-secondary-400 text-sm text-center">
                                As the company owner, you can reactivate your subscription to restore access.
                            </p>

                            <!-- Reactivate Button -->
                            <Link
                                :href="`/billing/reactivate?company_id=${company.id}`"
                                class="block w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white text-center px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
                            >
                                <svg class="w-5 h-5 inline-block mr-2 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Reactivate Subscription
                            </Link>

                            <!-- View Billing -->
                            <Link
                                :href="`/billing`"
                                class="block w-full bg-white dark:bg-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-600 text-secondary-700 dark:text-secondary-300 text-center px-6 py-3 rounded-lg font-medium border border-secondary-300 dark:border-secondary-600 transition-colors"
                            >
                                View Billing Details
                            </Link>
                        </div>
                    </div>

                    <!-- Team Member View -->
                    <div v-else class="space-y-4">
                        <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <div class="flex items-start">
                                <svg class="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                                </svg>
                                <div class="flex-1">
                                    <h3 class="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
                                        Access Restricted
                                    </h3>
                                    <p class="text-sm text-red-800 dark:text-red-300">
                                        This company's subscription has expired. Please contact the company owner to reactivate.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="bg-secondary-50 dark:bg-secondary-700/50 rounded-lg p-4">
                            <h3 class="text-sm font-semibold text-secondary-900 dark:text-white mb-2">
                                What can I do?
                            </h3>
                            <ul class="space-y-2 text-sm text-secondary-600 dark:text-secondary-400">
                                <li class="flex items-start">
                                    <svg class="w-4 h-4 text-secondary-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                    </svg>
                                    Contact the company owner to reactivate the subscription
                                </li>
                                <li class="flex items-start">
                                    <svg class="w-4 h-4 text-secondary-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                    </svg>
                                    Access other companies you're a member of
                                </li>
                                <li class="flex items-start">
                                    <svg class="w-4 h-4 text-secondary-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                    </svg>
                                    Contact support if you need assistance
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Action Buttons (Common) -->
                    <div class="mt-6 space-y-3">
                        <Link
                            href="/my-companies"
                            class="block w-full bg-primary-600 hover:bg-primary-700 text-white text-center px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all"
                        >
                            <svg class="w-5 h-5 inline-block mr-2 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            View My Companies
                        </Link>

                        <a
                            href="mailto:support@marginresolve.com"
                            class="block w-full bg-white dark:bg-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-600 text-secondary-700 dark:text-secondary-300 text-center px-6 py-3 rounded-lg font-medium border border-secondary-300 dark:border-secondary-600 transition-colors"
                        >
                            <svg class="w-5 h-5 inline-block mr-2 -mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Contact Support
                        </a>
                    </div>
                </div>
            </div>

            <!-- Footer Note -->
            <p class="mt-6 text-center text-sm text-secondary-600 dark:text-secondary-400">
                <a href="/" class="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium">
                    ← Back to Home
                </a>
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
    company: {
        type: Object,
        required: true
    }
});

// Computed
const statusLabel = computed(() => {
    const labels = {
        'suspended': 'Suspended',
        'cancelled': 'Cancelled'
    };
    return labels[props.company.status] || 'Unavailable';
});

const statusBadgeClass = computed(() => {
    const classes = {
        'suspended': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        'cancelled': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };
    return classes[props.company.status] || 'bg-gray-100 text-gray-800';
});
</script>
